import React, { useEffect, useId, useRef, useState } from "react";
import { Float, FloatVirtualInitialProps } from "@headlessui-float/react";
import { Transition } from "@headlessui/react";

interface ToolTipProps {
  text: string | React.ReactNode;
  children: React.ReactNode;
  /**
   * Legacy alias for maxWidth. The bubble always hugs short content and wraps
   * at the cap; it is never stretched to this width.
   */
  width?: string;
  /** Overrides the default 280px max width. */
  maxWidth?: string;
  minWidth?: string;
  /** @deprecated No-op. The tooltip is anchored to the trigger element now. */
  xOffset?: number;
  /** @deprecated No-op. The tooltip is anchored to the trigger element now. */
  yOffset?: number;
  /** "above" (default) or "below" - flips automatically when there is no room */
  placement?: "above" | "below";
  topLevelStyles?: React.CSSProperties;
  inline?: boolean;
}

const ENTER_DELAY_MS = 300;

// Placement of the tooltip is mirrored into the transform origin so the scale
// animation grows from the trigger. Literal class names so Tailwind compiles them.
const originClassResolver = (placement: string): string =>
  placement.split("-")[0] === "bottom" ? "origin-top" : "origin-bottom";

const ToolTip = ({
  text,
  children,
  width,
  maxWidth,
  minWidth,
  placement = "above",
  topLevelStyles = {},
  inline = false,
}: ToolTipProps) => {
  const [show, setShow] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const floatRefs = useRef<FloatVirtualInitialProps["refs"] | null>(null);
  const enterTimer = useRef<number | null>(null);
  const tooltipId = useId();

  const clearEnterTimer = () => {
    if (enterTimer.current !== null) {
      window.clearTimeout(enterTimer.current);
      enterTimer.current = null;
    }
  };

  useEffect(() => clearEnterTimer, []);

  // Anchor the bubble to the wrapper's first element child (the actual trigger),
  // not the wrapper itself: the wrapper is a block element and can be much wider
  // than the visible trigger. Re-measured on every position read so it stays
  // correct when children re-render.
  useEffect(() => {
    const wrapper = wrapperRef.current;
    const refs = floatRefs.current;
    if (!wrapper || !refs) return;
    refs.setPositionReference({
      getBoundingClientRect: () =>
        ((wrapper.firstElementChild as HTMLElement | null) ?? wrapper).getBoundingClientRect(),
      contextElement: wrapper,
    });
  });

  useEffect(() => {
    if (!show) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setShow(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [show]);

  // If no tooltip text, then don't render the tooltip
  if (!text || (typeof text === "string" && text.trim() === "")) {
    return <>{children}</>;
  }

  const containerStyles: React.CSSProperties = inline
    ? { ...topLevelStyles, display: "inline-block" }
    : topLevelStyles;

  const handleMouseEnter = () => {
    clearEnterTimer();
    enterTimer.current = window.setTimeout(() => setShow(true), ENTER_DELAY_MS);
  };

  const handleMouseLeave = () => {
    clearEnterTimer();
    setShow(false);
  };

  return (
    <>
      {/* Hover/focus handlers live on this wrapper so tooltips still work when
          the child is a disabled control that swallows pointer events. */}
      <div
        ref={wrapperRef}
        className="relative"
        style={containerStyles}
        aria-describedby={show ? tooltipId : undefined}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={(event) => {
          // Keyboard focus only: plain clicks also focus the trigger, and
          // flashing the tooltip on every click reads as noise.
          if (!event.target.matches(":focus-visible")) return;
          clearEnterTimer();
          setShow(true);
        }}
        onBlur={handleMouseLeave}
      >
        {children}
      </div>
      <Float.Virtual
        show={show}
        onInitial={({ refs }: FloatVirtualInitialProps) => {
          floatRefs.current = refs;
        }}
        // as="div" keeps the positioned wrapper a real element so the tw-boundary
        // class below scopes the panel's Tailwind utilities (the portal root is
        // outside the app's .tw-boundary). aquilo-tooltip-float is a raw rule in
        // input.css — a pointer-events-none utility cannot match this element
        // because it is itself the .tw-boundary.
        as="div"
        className="tw-boundary aquilo-tooltip-float"
        placement={placement === "below" ? "bottom" : "top"}
        offset={8}
        shift={{ padding: 8 }}
        flip
        arrow={8}
        portal
        // Above aquilo modals (z-[15001]) and the in-app MUI dialog band (up to 16000)
        zIndex={16001}
        enter="transition ease-out duration-100"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
        originClass={originClassResolver}
      >
        <Transition.Child
          as="div"
          // Float.Arrow needs its DOM ref when applying cached coordinates on reopen.
          // Keep the hidden panel mounted so that ref and positioning state stay in sync.
          unmount={false}
          id={tooltipId}
          role="tooltip"
          className="pointer-events-none w-max max-w-[280px] break-words rounded-lg bg-neutral-800 px-3 py-2 text-left text-[14px] font-normal leading-[20px] text-white shadow-[0px_4px_12px_0px_#10182814]"
          style={{ maxWidth: maxWidth ?? width, minWidth }}
        >
          {text}
          <Float.Arrow className="absolute h-2 w-2 rotate-45 bg-neutral-800" />
        </Transition.Child>
      </Float.Virtual>
    </>
  );
};

export default ToolTip;
